import React, { useEffect, useState, useRef, useCallback } from "react";
import api, { formatApiError, fileDownloadUrl } from "@/lib/api";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useCenter } from "@/context/CenterContext";
import { useRealtime } from "@/context/RealtimeContext";
import UserAvatar, { resolveImg } from "@/components/UserAvatar";
import { uploadFile } from "@/components/UploadButton";
import DocumentScanner from "@/components/DocumentScanner";
import ShiftReportDialog from "@/components/ShiftReportDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Send, Search, Pin, Plus, Users, ArrowLeft, Paperclip, FileText, Check, CheckCheck, Hash, Sparkles, Bot, X, Loader2, Camera, MapPin, ScanLine, ClipboardList, AlertTriangle, UserCog, ListChecks } from "lucide-react";
import { toast } from "sonner";

const fmtEst = (iso) => {
  try { return new Date(iso).toLocaleString("en-CA", { timeZone: "America/Montreal", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

function NewChatDialog({ open, onOpenChange, onCreated }) {
  const { t } = useT();
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState([]);
  const [name, setName] = useState("");
  const [isGroup, setIsGroup] = useState(false);
  useEffect(() => { if (open) { api.get("/employees").then((r) => setEmployees(r.data.filter((e) => e.id !== user.id))); setSelected([]); setName(""); setIsGroup(false); } }, [open, user.id]);
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const create = async () => {
    try {
      const type = isGroup || selected.length > 1 ? "group" : "private";
      const { data } = await api.post("/chat/channels", { name: type === "group" ? name || "Group" : null, type, members: selected });
      onCreated(data); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{t("newChat")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input type="checkbox" id="grp" checked={isGroup} onChange={(e) => setIsGroup(e.target.checked)} className="w-4 h-4" data-testid="is-group" />
            <label htmlFor="grp" className="text-sm">{t("newGroup")}</label>
          </div>
          {isGroup && <Input placeholder={t("groupName")} value={name} onChange={(e) => setName(e.target.value)} data-testid="group-name" />}
          <div className="max-h-64 overflow-y-auto scrollbar-thin space-y-1 border border-border rounded-lg p-2">
            {employees.map((e) => (
              <button key={e.id} onClick={() => toggle(e.id)} className={`w-full flex items-center gap-2 p-2 rounded-lg transition-colors ${selected.includes(e.id) ? "bg-primary/10" : "hover:bg-accent"}`} data-testid="chat-member-option">
                <UserAvatar name={e.name} src={e.profile_picture} size={30} />
                <span className="text-sm flex-1 text-left">{e.name}</span>
                {selected.includes(e.id) && <Check className="w-4 h-4 text-primary" />}
              </button>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
          <Button onClick={create} disabled={selected.length === 0} data-testid="create-chat">{t("create")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageGroupDialog({ open, onOpenChange, channel, onSaved }) {
  const { t } = useT();
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open && channel) {
      api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
      setSelected(channel.members || []);
      setName(channel.name || "");
    }
  }, [open, channel]);
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const save = async () => {
    if (!channel) return;
    setSaving(true);
    try {
      await api.put(`/chat/channels/${channel.id}`, { name: name.trim() || channel.name, members: selected });
      toast.success(t("save")); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{t("manageGroup")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder={t("groupName")} value={name} onChange={(e) => setName(e.target.value)} data-testid="manage-group-name" />
          <p className="text-xs text-muted-foreground">{t("addRemoveMembers")}</p>
          <div className="max-h-64 overflow-y-auto scrollbar-thin space-y-1 border border-border rounded-lg p-2">
            {employees.map((e) => (
              <button key={e.id} onClick={() => toggle(e.id)} className={`w-full flex items-center gap-2 p-2 rounded-lg transition-colors ${selected.includes(e.id) ? "bg-primary/10" : "hover:bg-accent"}`} data-testid="manage-member-option">
                <UserAvatar name={e.name} src={e.profile_picture} size={30} />
                <span className="text-sm flex-1 text-left">{e.name}</span>
                {selected.includes(e.id) && <Check className="w-4 h-4 text-primary" />}
              </button>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
          <Button onClick={save} disabled={saving || selected.length === 0} data-testid="save-manage-group">{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GridMentionDialog({ open, onOpenChange, onEntered }) {
  const { t } = useT();
  const [q, setQ] = useState("");
  const [residents, setResidents] = useState([]);
  const [resident, setResident] = useState(null);
  const [grids, setGrids] = useState([]);
  const [grid, setGrid] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) { setQ(""); setResident(null); setGrid(null); setGrids([]); setNote(""); api.get("/residents").then((r) => setResidents(r.data)).catch(() => {}); }
  }, [open]);
  const pickResident = async (r) => {
    setResident(r); setGrid(null);
    const { data } = await api.get("/grids", { params: { resident_id: r.id, status: "active" } });
    setGrids(data);
  };
  const pickCode = async (code) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/grids/${grid.id}/entries`, { code, note });
      await onEntered({ ...data }, resident.full_name);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const filtered = residents.filter((r) => (r.full_name || "").toLowerCase().includes(q.toLowerCase()) || (r.user_code || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ListChecks className="w-5 h-5 text-emerald-600" />{t("ratingGrids")}</DialogTitle></DialogHeader>
        {!resident ? (
          <div className="space-y-2">
            <Input autoFocus placeholder={`@ ${t("search")}`} value={q} onChange={(e) => setQ(e.target.value)} data-testid="mention-search" />
            <div className="max-h-64 overflow-y-auto scrollbar-thin space-y-1">
              {filtered.map((r) => (
                <button key={r.id} onClick={() => pickResident(r)} className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-accent text-left" data-testid="mention-resident">
                  <UserAvatar name={r.full_name} src={r.photo} size={30} />
                  <span className="text-sm flex-1">{r.full_name}</span>
                  {r.user_code && <span className="text-[10px] text-primary">{r.user_code}</span>}
                </button>
              ))}
              {filtered.length === 0 && <p className="text-sm text-muted-foreground text-center py-3">{t("noData")}</p>}
            </div>
          </div>
        ) : !grid ? (
          <div className="space-y-2">
            <button onClick={() => setResident(null)} className="text-xs text-muted-foreground flex items-center gap-1"><ArrowLeft className="w-3 h-3" />{resident.full_name}</button>
            {grids.length === 0 ? <p className="text-sm text-muted-foreground text-center py-3">{t("noGrids")}</p> :
              grids.map((g) => <button key={g.id} onClick={() => setGrid(g)} className="w-full p-2.5 rounded-lg border border-border hover:bg-accent text-left text-sm font-medium" data-testid="mention-grid">{g.name}</button>)}
          </div>
        ) : (
          <div className="space-y-2">
            <button onClick={() => setGrid(null)} className="text-xs text-muted-foreground flex items-center gap-1"><ArrowLeft className="w-3 h-3" />{resident.full_name} · {grid.name}</button>
            <Input placeholder={t("note")} value={note} onChange={(e) => setNote(e.target.value)} data-testid="mention-note" />
            <p className="text-xs text-muted-foreground">{t("selectCode")} :</p>
            <div className="grid grid-cols-2 gap-2">
              {grid.codes.map((c) => (
                <button key={c.code} disabled={saving} onClick={() => pickCode(c.code)} className="p-2 rounded-lg border border-border hover:bg-emerald-500/10 text-left" data-testid="mention-code">
                  <span className="font-bold text-emerald-600">{c.code}</span> <span className="text-sm">= {c.meaning}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Attachment({ att }) {
  const url = fileDownloadUrl(att.file_id);
  const isImg = (att.content_type || "").startsWith("image/");
  const meta = att.meta;
  const caption = meta && (
    <div className="flex items-center flex-wrap gap-x-1.5 mt-1 text-[10px] text-muted-foreground" data-testid="attachment-meta">
      <span>{fmtEst(meta.captured_at)} EST</span>
      {meta.employee_name && <span>· {meta.employee_name}</span>}
      {meta.center_name && <span>· {meta.center_name}</span>}
      {(meta.lat != null && meta.lng != null) && (
        <span className="inline-flex items-center gap-0.5" title={`${Number(meta.lat).toFixed(5)}, ${Number(meta.lng).toFixed(5)}`}><MapPin className="w-2.5 h-2.5" />GPS</span>
      )}
    </div>
  );
  if (isImg) return <div className="mt-1"><a href={url} target="_blank" rel="noreferrer"><img src={url} alt={att.name} className="rounded-lg max-w-[220px] max-h-52 object-cover" /></a>{caption}</div>;
  return <div className="mt-1"><a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-2 rounded-lg bg-black/5 dark:bg-white/10 text-sm"><FileText className="w-4 h-4" />{att.name}</a>{caption}</div>;
}

export default function Chat() {
  const { t } = useT();
  const { user } = useAuth();
  const { role, isSuperAdmin } = useCenter();
  const isAdmin = isSuperAdmin || role === "admin";
  const { subscribe, send, loadNotifications } = useRealtime();
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [search, setSearch] = useState("");
  const [msgSearch, setMsgSearch] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const [pendingAtt, setPendingAtt] = useState([]);
  const [aiOpen, setAiOpen] = useState(false);
  const [geoEnabled, setGeoEnabled] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [srOpen, setSrOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [gridMentionOpen, setGridMentionOpen] = useState(false);
  const camRef = useRef();
  const galRef = useRef();
  const scrollRef = useRef();
  const typingTimer = useRef();

  useEffect(() => { api.get("/timeclock/config").then((r) => setGeoEnabled(!!r.data.geo_enabled)).catch(() => {}); }, []);

  const buildMeta = () => new Promise((resolve) => {
    const meta = { captured_at: new Date().toISOString(), device: (navigator.userAgent || "").slice(0, 140) };
    if (geoEnabled && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => { meta.lat = p.coords.latitude; meta.lng = p.coords.longitude; resolve(meta); },
        () => resolve(meta), { timeout: 6000, maximumAge: 60000 });
    } else resolve(meta);
  });

  const handleFiles = async (fileList, kind) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    const meta = await buildMeta();
    for (const f of files) {
      try {
        const d = await uploadFile(f, "chat");
        setPendingAtt((p) => [...p, { file_id: d.id, name: d.original_filename, content_type: d.content_type, kind, meta }]);
      } catch { toast.error("Upload failed"); }
    }
    setUploading(false);
  };

  const handleScanSend = async (file) => {
    setUploading(true);
    const meta = await buildMeta();
    try {
      const d = await uploadFile(file, "chat");
      setPendingAtt((p) => [...p, { file_id: d.id, name: d.original_filename, content_type: d.content_type, kind: "scan", meta }]);
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const scrollToMsg = (id) => {
    const el = document.getElementById(`msg-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ai-msg-highlight");
      setTimeout(() => el.classList.remove("ai-msg-highlight"), 2200);
    }
  };

  const loadChannels = useCallback(async () => { const { data } = await api.get("/chat/channels"); setChannels(data); return data; }, []);
  useEffect(() => { loadChannels(); }, [loadChannels]);

  const openChannel = useCallback(async (ch) => {
    setActive(ch); setMsgSearch(""); setPendingAtt([]);
    const { data } = await api.get(`/chat/channels/${ch.id}/messages`);
    setMessages(data);
    await api.post(`/chat/channels/${ch.id}/read`);
    loadChannels();
  }, [loadChannels]);

  // realtime
  useEffect(() => subscribe((msg) => {
    if (msg.event === "message") {
      if (active && msg.data.channel_id === active.id) {
        setMessages((m) => m.some((x) => x.id === msg.data.id) ? m : [...m, msg.data]);
        api.post(`/chat/channels/${active.id}/read`);
      }
      loadChannels();
    } else if (msg.event === "channel_updated") {
      loadChannels().then((data) => {
        if (active && !data.find((c) => c.id === active.id)) setActive(null);
      });
    } else if (msg.event === "typing" && active && msg.data.channel_id === active.id && msg.data.user_id !== user.id) {
      setTypingUser(msg.data.name);
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => setTypingUser(null), 3000);
    }
  }), [subscribe, active, user.id, loadChannels]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, typingUser]);

  const doSearch = async () => {
    if (!active) return;
    const { data } = await api.get(`/chat/channels/${active.id}/messages`, { params: msgSearch ? { search: msgSearch } : {} });
    setMessages(data);
  };
  useEffect(() => { if (active) { const tm = setTimeout(doSearch, 300); return () => clearTimeout(tm); } }, [msgSearch]); // eslint-disable-line

  const sendMsg = async () => {
    if ((!text.trim() && pendingAtt.length === 0) || !active) return;
    const payload = { text: text.trim() || null, attachments: pendingAtt };
    setText(""); setPendingAtt([]);
    try { const { data } = await api.post(`/chat/channels/${active.id}/messages`, payload); setMessages((m) => m.some((x) => x.id === data.id) ? m : [...m, data]); loadChannels(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onType = (v) => {
    if (v.endsWith("@") && !text.endsWith("@")) { setText(v.slice(0, -1)); setGridMentionOpen(true); return; }
    setText(v); if (active) send({ event: "typing", channel_id: active.id });
  };
  const sendGridEntry = async (entry, residentName) => {
    if (!active) return;
    const meta = { resident_id: entry.resident_id, resident_name: residentName, grid_id: entry.grid_id,
      grid_name: entry.grid_name, code: entry.code, meaning: entry.meaning, entry_at: entry.entry_at,
      employee_name: entry.employee_name, note: entry.note };
    try { const { data } = await api.post(`/chat/channels/${active.id}/messages`, { text: null, grid_entry: meta }); setMessages((m) => m.some((x) => x.id === data.id) ? m : [...m, data]); loadChannels(); toast.success(t("save")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const togglePin = async (ch, e) => { e.stopPropagation(); await api.post(`/chat/channels/${ch.id}/pin`); loadChannels(); };

  const channelName = (ch) => ch.type === "private" ? (ch.display_name || t("chat")) : ch.name;
  const filteredChannels = channels.filter((c) => channelName(c)?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="h-full flex">
      {/* Channel list */}
      <div className={`w-full sm:w-80 border-r border-border flex flex-col bg-card ${active ? "hidden sm:flex" : "flex"}`}>
        <div className="p-4 flex items-center justify-between">
          <h1 className="font-display text-xl font-bold">{t("chat")}</h1>
          <Button size="icon" variant="outline" onClick={() => setNewOpen(true)} data-testid="new-chat-button"><Plus className="w-4 h-4" /></Button>
        </div>
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} className="pl-9 h-9" data-testid="channel-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin">
          {filteredChannels.map((ch) => (
            <button key={ch.id} onClick={() => openChannel(ch)} data-testid="channel-item"
              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-accent/60 transition-colors border-b border-border/50 ${active?.id === ch.id ? "bg-accent" : ""}`}>
              {ch.type === "group"
                ? <div className="w-11 h-11 rounded-full bg-primary/15 flex items-center justify-center shrink-0"><Hash className="w-5 h-5 text-primary" /></div>
                : <UserAvatar name={ch.display_name} src={ch.display_avatar} size={44} />}
              <div className="flex-1 min-w-0 text-left">
                <div className="flex items-center gap-1.5">
                  {ch.pinned && <Pin className="w-3 h-3 text-muted-foreground shrink-0" />}
                  <p className="font-medium truncate">{channelName(ch)}</p>
                </div>
                <p className="text-xs text-muted-foreground truncate">{ch.last_message?.text || (ch.last_message?.attachments?.length ? "📎" : "")}</p>
              </div>
              {ch.unread > 0 && <span className="min-w-5 h-5 px-1.5 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center">{ch.unread}</span>}
              <span onClick={(e) => togglePin(ch, e)} className="opacity-0 group-hover:opacity-100"><Pin className={`w-3.5 h-3.5 ${ch.pinned ? "text-primary" : "text-muted-foreground"}`} /></span>
            </button>
          ))}
        </div>
      </div>

      {/* Conversation */}
      <div className={`flex-1 relative bg-background overflow-hidden ${active ? "flex" : "hidden sm:flex"}`}>
        <div className="flex-1 flex flex-col min-w-0">
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
            <Users className="w-12 h-12 mb-3 opacity-40" />
            <p>{t("selectConversation")}</p>
          </div>
        ) : (
          <>
            <div className="h-16 border-b border-border flex items-center gap-3 px-4 bg-card">
              <button className="sm:hidden" onClick={() => setActive(null)} data-testid="back-to-channels"><ArrowLeft className="w-5 h-5" /></button>
              {active.type === "group" ? <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center"><Hash className="w-4 h-4 text-primary" /></div> : <UserAvatar name={active.display_name} src={active.display_avatar} size={36} />}
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{channelName(active)}</p>
                {active.type === "group" && <p className="text-xs text-muted-foreground">{active.members?.length} {t("members")}</p>}
              </div>
              <div className="relative hidden sm:block">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={msgSearch} onChange={(e) => setMsgSearch(e.target.value)} placeholder={t("searchMessages")} className="pl-8 h-8 w-44" data-testid="message-search" />
              </div>
              <Button size="icon" variant="ghost" onClick={(e) => togglePin(active, e)} data-testid="pin-channel"><Pin className={`w-4 h-4 ${active.pinned ? "text-primary fill-primary" : ""}`} /></Button>
              {active.type === "group" && isAdmin && (
                <Button size="icon" variant="ghost" onClick={() => setManageOpen(true)} data-testid="manage-group-button" title={t("manageGroup")}>
                  <UserCog className="w-4 h-4" />
                </Button>
              )}
              {active.type === "group" && (
                <Button size="icon" variant={aiOpen ? "default" : "ghost"} onClick={() => setAiOpen((v) => !v)} data-testid="group-ai-toggle" title={t("askGroupAI")}>
                  <Sparkles className="w-4 h-4" />
                </Button>
              )}
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-2" style={{ backgroundImage: "radial-gradient(hsl(var(--border)/0.4) 0.5px, transparent 0.5px)", backgroundSize: "18px 18px" }}>
              {messages.map((m) => {
                const mine = m.sender_id === user.id;
                const readByOthers = (m.read_by || []).length > 1;
                return (
                  <div key={m.id} id={`msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"} rounded-2xl transition-shadow`} data-testid="chat-message">
                    <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                      {!mine && active.type === "group" && <span className="text-[11px] text-muted-foreground ml-1 mb-0.5">{m.sender_name}</span>}
                      {m.grid_entry ? (
                        <div className="rounded-2xl border border-emerald-400/40 bg-card overflow-hidden w-full max-w-[380px]" data-testid="grid-entry-message">
                          <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border-b border-emerald-500/20">
                            <ListChecks className="w-4 h-4 text-emerald-600" />
                            <span className="text-sm font-semibold flex-1 truncate">{m.grid_entry.resident_name} · {m.grid_entry.grid_name}</span>
                          </div>
                          <div className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className="w-9 h-9 shrink-0 rounded-lg bg-emerald-500/15 text-emerald-700 flex items-center justify-center font-bold">{m.grid_entry.code}</span>
                              <div className="min-w-0"><p className="text-sm font-medium">{m.grid_entry.meaning}</p>
                                <p className="text-[11px] text-muted-foreground">{new Date(m.grid_entry.entry_at).toLocaleString()} · {m.grid_entry.employee_name}</p></div>
                            </div>
                            {m.grid_entry.note && <p className="text-sm mt-1.5">{m.grid_entry.note}</p>}
                          </div>
                        </div>
                      ) : m.type === "shift_report" ? (
                        <div className="rounded-2xl border border-primary/30 bg-card overflow-hidden w-full max-w-[420px]" data-testid="shift-report-message">
                          <div className="flex items-center gap-2 px-3 py-2 bg-primary/10 border-b border-primary/20">
                            <ClipboardList className="w-4 h-4 text-primary" />
                            <span className="text-sm font-semibold flex-1">{t("shiftReport")}</span>
                            {m.report_urgent && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-destructive/15 text-destructive font-semibold inline-flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" />{t("urgent")}</span>}
                          </div>
                          {m.report_summary && (
                            <div className="px-3 py-2 border-b border-border/50 bg-primary/[0.03]">
                              <p className="text-[11px] font-semibold text-primary flex items-center gap-1"><Sparkles className="w-3 h-3" />{t("aiSummary")}</p>
                              <p className="text-sm mt-0.5">{m.report_summary}</p>
                            </div>
                          )}
                          <div className="px-3 py-2">
                            <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                            {(m.attachments || []).map((a, i) => <Attachment key={i} att={a} />)}
                          </div>
                          <div className="px-3 pb-1.5 text-[10px] text-muted-foreground text-right">{m.created_at && new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                        </div>
                      ) : (
                        <div className={`rounded-2xl px-3.5 py-2 ${mine ? "bg-primary text-white rounded-br-sm" : "bg-card border border-border rounded-bl-sm"}`}>
                          {m.text && <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>}
                          {(m.attachments || []).map((a, i) => <Attachment key={i} att={a} />)}
                          <div className={`flex items-center gap-1 justify-end mt-0.5 text-[10px] ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                            {m.created_at && new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            {mine && (readByOthers ? <CheckCheck className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {typingUser && <p className="text-xs text-muted-foreground ml-2 italic">{typingUser} {t("typing")}</p>}
            </div>

            {pendingAtt.length > 0 && (
              <div className="px-4 py-2 border-t border-border flex gap-2 flex-wrap bg-card">
                {pendingAtt.map((a, i) => {
                  const isImg = (a.content_type || "").startsWith("image/");
                  return (
                    <div key={i} className="relative" data-testid="pending-attachment">
                      {isImg
                        ? <img src={fileDownloadUrl(a.file_id)} alt={a.name} className="w-16 h-16 object-cover rounded-lg border border-border" />
                        : <div className="w-16 h-16 rounded-lg border border-border flex flex-col items-center justify-center bg-muted text-[9px] p-1 text-center"><FileText className="w-5 h-5 mb-0.5" /><span className="truncate w-full">{a.name}</span></div>}
                      <button onClick={() => setPendingAtt((p) => p.filter((_, j) => j !== i))} data-testid="remove-attachment"
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-destructive text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
                    </div>
                  );
                })}
                {uploading && <div className="w-16 h-16 rounded-lg border border-dashed border-border flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>}
              </div>
            )}
            <div className="p-3 pr-16 sm:pr-20 border-t border-border bg-card flex items-center gap-2">
              <input ref={camRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => { handleFiles(e.target.files, "photo"); e.target.value = ""; }} />
              <input ref={galRef} type="file" accept="image/*,application/pdf,.doc,.docx" multiple className="hidden" onChange={(e) => { handleFiles(e.target.files, "file"); e.target.value = ""; }} />
              <Button size="icon" variant="ghost" onClick={() => camRef.current?.click()} disabled={uploading} title={t("takePhoto")} data-testid="chat-camera"><Camera className="w-5 h-5" /></Button>
              <Button size="icon" variant="ghost" onClick={() => galRef.current?.click()} disabled={uploading} title={t("attachFile")} data-testid="chat-attach"><Paperclip className="w-5 h-5" /></Button>
              <Button size="icon" variant="ghost" onClick={() => setScanOpen(true)} disabled={uploading} title={t("scanDocument")} data-testid="chat-scan"><ScanLine className="w-5 h-5" /></Button>
              {active?.type === "group" && (
                <Button size="icon" variant="ghost" onClick={() => setSrOpen(true)} title={t("submitReport")} data-testid="chat-shift-report"><ClipboardList className="w-5 h-5" /></Button>
              )}
              <Button size="icon" variant="ghost" onClick={() => setGridMentionOpen(true)} title={t("ratingGrids")} data-testid="chat-grid-mention"><ListChecks className="w-5 h-5" /></Button>
              <Input value={text} onChange={(e) => onType(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), sendMsg())}
                placeholder={t("typeMessage")} className="flex-1" data-testid="message-input" />
              <Button size="icon" onClick={sendMsg} data-testid="send-message"><Send className="w-4 h-4" /></Button>
            </div>
          </>
        )}
        </div>
        {active && active.type === "group" && aiOpen && (
          <GroupAIPanel channelId={active.id} channelName={channelName(active)} onClose={() => setAiOpen(false)} onJump={scrollToMsg} />
        )}
      </div>

      <NewChatDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(ch) => { loadChannels(); openChannel(ch); }} />
      <ManageGroupDialog open={manageOpen} onOpenChange={setManageOpen} channel={active}
        onSaved={async () => { const data = await loadChannels(); const fresh = data.find((c) => c.id === active?.id); if (fresh) setActive(fresh); }} />
      <GridMentionDialog open={gridMentionOpen} onOpenChange={setGridMentionOpen} onEntered={sendGridEntry} />
      <DocumentScanner open={scanOpen} onClose={() => setScanOpen(false)} onSend={handleScanSend} />
      <ShiftReportDialog open={srOpen} onClose={() => setSrOpen(false)} channelId={active?.id} onSubmitted={() => { if (active) openChannel(active); }} />
    </div>
  );
}

function Badge({ children }) {
  return <span className="text-xs px-2 py-1 rounded bg-muted flex items-center gap-1"><Paperclip className="w-3 h-3" />{children}</span>;
}

function GroupAIPanel({ channelId, channelName, onClose, onJump }) {
  const { t } = useT();
  const [thread, setThread] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef();

  useEffect(() => { setThread([]); setQ(""); }, [channelId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread, loading]);

  const ask = async (question) => {
    const query = (question ?? q).trim();
    if (!query || loading) return;
    setThread((tr) => [...tr, { role: "user", text: query }]);
    setQ(""); setLoading(true);
    try {
      const { data } = await api.post(`/chat/channels/${channelId}/ask`, { question: query });
      setThread((tr) => [...tr, { role: "ai", text: data.answer, references: data.references || [], urgent: data.urgent }]);
    } catch (e) {
      setThread((tr) => [...tr, { role: "ai", text: formatApiError(e.response?.data?.detail), references: [] }]);
    } finally { setLoading(false); }
  };

  const examples = [t("ex1"), t("ex2"), t("ex3"), t("ex4")];

  return (
    <div className="absolute inset-0 z-20 sm:static sm:z-auto sm:w-96 flex flex-col border-l border-border bg-card shrink-0" data-testid="group-ai-panel">
      <div className="h-16 border-b border-border flex items-center gap-2.5 px-4">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center">
          <Bot className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{t("groupAI")}</p>
          <p className="text-[11px] text-muted-foreground truncate">{channelName}</p>
        </div>
        <Button size="icon" variant="ghost" onClick={onClose} data-testid="group-ai-close"><X className="w-4 h-4" /></Button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-3">
        {thread.length === 0 && (
          <div className="space-y-3">
            <div className="p-3 rounded-xl bg-primary/5 border border-primary/20">
              <div className="flex items-center gap-1.5 mb-1 text-primary font-medium text-sm"><Sparkles className="w-4 h-4" />{t("groupAI")}</div>
              <p className="text-xs text-muted-foreground leading-relaxed">{t("groupAiIntro")}</p>
            </div>
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide px-1">{t("tryExamples")}</p>
            <div className="flex flex-col gap-1.5">
              {examples.map((ex, i) => (
                <button key={i} onClick={() => ask(ex)} data-testid="group-ai-example"
                  className="text-left text-xs px-3 py-2 rounded-lg border border-border hover:bg-accent hover:border-primary/40 transition-colors">
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}

        {thread.map((m, i) => (
          m.role === "user" ? (
            <div key={i} className="flex justify-end">
              <div className="max-w-[85%] bg-primary text-white rounded-2xl rounded-br-sm px-3.5 py-2 text-sm">{m.text}</div>
            </div>
          ) : (
            <div key={i} className="flex justify-start">
              <div className="max-w-[92%] space-y-2">
                <div className={`rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm border ${m.urgent ? "border-destructive/40 bg-destructive/5" : "border-border bg-background"}`}>
                  {m.urgent && <div className="flex items-center gap-1 text-destructive text-[11px] font-semibold mb-1"><Sparkles className="w-3 h-3" />{t("urgentFlag")}</div>}
                  <p className="whitespace-pre-wrap leading-relaxed" data-testid="group-ai-answer">{m.text}</p>
                </div>
                {m.references?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase px-1">{t("sources")}</p>
                    {m.references.map((r, j) => (
                      <button key={j} onClick={() => onJump(r.id)} data-testid="group-ai-source"
                        className="w-full text-left text-[11px] px-2.5 py-1.5 rounded-lg bg-muted hover:bg-accent transition-colors flex items-start gap-1.5">
                        <Hash className="w-3 h-3 mt-0.5 shrink-0 text-primary" />
                        <span className="min-w-0"><b>{r.label}:</b> <span className="text-muted-foreground">{r.snippet}</span></span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        ))}

        {loading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground px-1">
            <Loader2 className="w-4 h-4 animate-spin" />{t("aiThinking")}
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="p-3 border-t border-border flex items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), ask())}
          placeholder={t("askAnything")} className="flex-1" data-testid="group-ai-input" disabled={loading} />
        <Button size="icon" onClick={() => ask()} disabled={loading || !q.trim()} data-testid="group-ai-send"><Send className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
